/* ---- phones first: one column, heading, form, next steps, direct contacts ---- */
.page {
  position: relative;
  isolation: isolate;
  padding-block: var(--sp-8) var(--sp-16);
}
/* The road photograph behind the page, darkened enough for the heading and the glass. */
.page::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -2;
  background: url("/images/site/contact-road-960.jpg") center 20% / cover;
}
.page::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(
    180deg,
    rgba(17, 18, 19, 0.92) 0%,
    rgba(17, 18, 19, 0.55) 35%,
    rgba(17, 18, 19, 0.6) 70%,
    var(--surface) 100%
  );
}
.layout {
  display: grid;
  gap: var(--sp-8);
}
.intro {
  display: grid;
  gap: var(--sp-3);
}
.title {
  margin: 0;
  font-size: clamp(var(--fs-2xl), 1rem + 2.4vw, var(--fs-3xl));
}
.lead {
  max-width: 34rem;
  margin: 0;
  font-size: var(--fs-lg);
  color: var(--text);
}
/* The form's panel: frosted glass over the road photograph, so the page keeps its
   atmosphere. Only the panel is see-through; the fields inside get a near-solid dark fill
   and a brighter border (the token overrides below), so what people type keeps full
   contrast whatever the photo does behind it. */
.panel {
  --surface-raised: rgba(9, 12, 16, 0.78);
  --border-strong: rgba(255, 255, 255, 0.26);
  --text-muted: #a3adb8;
  padding: var(--sp-6) var(--sp-5);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: var(--radius-lg);
  background: rgba(22, 26, 32, 0.5);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 24px 60px rgba(0, 0, 0, 0.45);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  backdrop-filter: blur(12px) saturate(140%);
}
/* Without backdrop-filter the glass would show the photo sharp behind the fields. */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .panel {
    background: rgba(22, 26, 32, 0.92);
  }
}
.aside {
  display: grid;
  gap: var(--sp-8);
}
.block {
  display: grid;
  gap: var(--sp-4);
}
.blockTitle {
  margin: 0;
  font-size: var(--fs-lg);
}
/* A real sequence, so numbered: the marker is the step's number in a ring. */
.steps {
  display: grid;
  gap: var(--sp-5);
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
}
.steps li {
  display: grid;
  grid-template-columns: 2rem 1fr;
  column-gap: var(--sp-3);
  counter-increment: step;
}
.steps li::before {
  content: counter(step);
  grid-row: span 2;
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: 1.5px solid var(--accent);
  border-radius: 50%;
  color: var(--accent-text);
  font-size: var(--fs-sm);
  font-weight: 700;
}
.steps strong {
  color: var(--text-strong);
}
.steps span {
  color: var(--text);
}
.direct {
  display: grid;
  gap: var(--sp-2);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
}
.direct a {
  justify-self: start;
  color: var(--text-strong);
}
.offices {
  display: grid;
  gap: var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--text);
}
.offices strong {
  display: block;
  color: var(--text-strong);
}

/* ---- tablets: the panel becomes a card ---------------------------------------- */
@media (min-width: 48rem) {
  .page {
    padding-block: var(--sp-12) var(--sp-20);
  }
  .panel {
    padding: var(--sp-8);
  }
  .aside {
    grid-template-columns: 1fr 1fr;
  }
}

/* ---- wide screens: heading and next steps left, the form right over the photo --- */
@media (min-width: 64rem) {
  .page {
    /* Runs up behind the floating header, as the home hero does, then gives the space
       back so the heading clears it. */
    margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
    padding-top: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-16));
  }
  .page::before {
    inset: 0 0 0 40%;
    background-image: url("/images/site/contact-road-1600.jpg");
    background-position: center;
  }
  .page::after {
    background: linear-gradient(
      90deg,
      var(--surface) 40%,
      rgba(17, 18, 19, 0.45) 56%,
      rgba(17, 18, 19, 0.1) 100%
    );
  }
  .layout {
    grid-template-columns: minmax(0, 1fr) minmax(0, 40rem);
    grid-template-areas:
      "intro panel"
      "aside panel";
    align-items: start;
    column-gap: clamp(var(--sp-12), 6vw, var(--sp-24));
    row-gap: var(--sp-12);
  }
  .intro {
    grid-area: intro;
  }
  .panel {
    grid-area: panel;
  }
  .aside {
    grid-area: aside;
    grid-template-columns: 1fr;
    max-width: 30rem;
  }
}
